import { t } from '../../utils/i18n';
import { isPageHidden } from '../../utils/checkHiddenPage';
import { SCREEN } from '../../utils/constants';

import {
    appointmentChannelId,
    form,
    hiddenPages,
    isBookAppointment,
    isFlowForCalendar,
    isInInstantQuote,
    procedures,
    providers,
    selectedLocation,
    selectedProcedures,
    selectedDeliveryMode,
    setSelectedDeliveryMode,
    setIsInstantQuote,
    staffId,
    staffProcedures,
    setStaffId,
    isAutoSelectSoonestProviderEnabled,
} from '../../utils/variables';
import { formId, setAppointmentChannelId } from '../../utils/variables';
import { getFormByFormId } from '../../services/formServices';
import { toggleLoading } from '../actions/loading';
import { loadCategories } from '../../views/components/category';
import { loadProcedures } from '../../views/components/procedures';
import { loadProviderList } from '../../views/pages/providers';
import { selectLocation } from '../actions/locationActions';
import { renderContinueBtn } from '../actions/providerActions';
import { navigate, refreshProvidersStore } from './movePages';
import { deliveryModeListeners } from './deliveryModeListeners';
import { patientContinueBtnHandler, patientListeners } from './patientListeners';
import { filterProcedures, handleProviderContinueBtn, handleSelectProvider, providerListeners } from './providersListeners';

// Multi-channel widgets (`appointment_channel_ids`) boot with only the DEFAULT
// channel's config, and each appointment channel is bound to exactly one
// location. Picking any other location must swap in that location's channel —
// otherwise the provider list filters the wrong channel's roster by the chosen
// location and always comes up empty ("No Providers Found").
const channelConfigCache = {};
export async function switchChannelForLocation(locationId) {
    const ids = form?.appointment_channel_ids;
    if (!Array.isArray(ids) || ids.length < 2 || !locationId) return;
    if (form?.appointment_channel?.location?.id === locationId) return;
    for (const id of ids) {
        if (id === form?.appointment_channel?.id) continue;
        let channel = channelConfigCache[id];
        if (!channel) {
            try {
                const res = await getFormByFormId(formId, id);
                channel = res?.data?.appointment_channel;
            } catch (e) {
                continue;
            }
            if (channel) channelConfigCache[id] = channel;
        }
        if (channel?.location?.id === locationId) {
            form.appointment_channel = channel;
            setAppointmentChannelId(channel.id);
            refreshProvidersStore(true);
            return;
        }
    }
}

export function locationListeners() {
    /* on select location*/
    document
        .querySelectorAll('.locations__container .location')
        .forEach((i) => {
            i.addEventListener('click', () => {
                const id = parseInt(i.getAttribute('data-location-id'));
                selectLocation(id);
            });
        });

    /* on click Continue after select location */
    document
        .querySelector('.locationContinue__btn')
        .addEventListener('click', async () => {
            // Re-prompt the delivery-mode step on every fresh Continue from the
            // location step so the gate in handleLocationPage() shows it again.
            if (form?.appointment_channel?.delivery_mode_selection) {
                setSelectedDeliveryMode(null);
            }
            toggleLoading(true);
            try {
                await switchChannelForLocation(selectedLocation);
            } finally {
                toggleLoading(false);
            }
            handleLocationPage();
        });

    /* on click back button */
    document
        .querySelector('.locations__container .backOneStep__btn')
        .addEventListener('click', () => {
            setIsInstantQuote(false);
            setSelectedDeliveryMode(null);
            document.querySelector('.no-preference-provider')?.classList?.add('hidden');
        });

    if (appointmentChannelId) {
        document
            .querySelector('.locations__container .backOneStep__btn').classList.add("hide")
    }
}


export const handleLocationPage = () => {
    console.log('[DBG] handleLocationPage, selectedLocation=', selectedLocation, 'providers=', JSON.stringify(providers?.map?.(p=>({id:p.id,loc:p.location_ids}))));
    if (isBookAppointment || appointmentChannelId) {
        // Gated "How would you like to meet?" step — after location, before
        // service. Only when the channel opts in and the visitor hasn't chosen
        // yet. The mode step's Continue re-enters handleLocationPage() with the
        // mode set, so it falls through to the normal provider/service routing.
        // deliveryModeListeners() returns false when the channel has a single
        // delivery bucket — the mode was auto-selected, skip the step.
        if (form?.appointment_channel?.delivery_mode_selection && !selectedDeliveryMode) {
            if (deliveryModeListeners()) {
                navigate(SCREEN.DELIVERY_MODE);
                return;
            }
        }
        // Auto-select soonest provider: skip the provider picker entirely and
        // engage the existing "any provider" (-1) path so the server assigns the
        // soonest-available provider for the chosen slot.
        // Only short-circuit once the service is resolved — the visitor already
        // picked one, or the channel offers a single service. With multiple
        // services and none selected, fall through to the routing below
        // (PATIENTS → services page) so the visitor picks a service first;
        // the services Continue re-enters handleLocationPage() with
        // `selectedProcedures` set and the gate then fires. Otherwise the
        // booking-payload fallback attaches the channel's ENTIRE service menu
        // to the event (see selectTimeActions.js empty-selection fallback).
        // Count DISTINCT ids: the payload serializes one entry per
        // channel-location pivot row, so a 1-service/2-location channel has 2
        // entries and a naive length check would wrongly show a 1-item menu.
        if (isAutoSelectSoonestProviderEnabled()) {
            const distinctProcedureIds = new Set(
                (form?.appointment_channel?.procedures || [])
                    .filter((p) => p?.online_booking)
                    .map((p) => p?.id)
            );
            if (selectedProcedures?.length || distinctProcedureIds.size <= 1) {
                setStaffId(-1);
                handleSelectProvider(null, -1);
                handleProviderContinueBtn();
                return;
            }
        }
        const hostType = form?.appointment_channel?.host || 'one_on_one';
        const staffsOfAppointmentChannel = form?.appointment_channel?.staffs?.filter(staff => staff?.online_booking);
        const staffCount = staffsOfAppointmentChannel?.length || 0;
        const canChooseStaff = (hostType === 'one_on_one' && staffCount > 1) ||
            (hostType !== 'one_on_one' && form?.appointment_channel?.visitor_can_choose_the_staff);

        const isProviderFirst = form?.appointment_channel?.is_select_provider_first;
        if (appointmentChannelId && (canChooseStaff || form?.appointment_channel?.procedures?.length > 1)) {
            const { procedures } = form.appointment_channel
            const staffsOfAppointmentChannel = form?.appointment_channel?.staffs?.filter(staff => staff?.online_booking)
            const ids = staffsOfAppointmentChannel?.map(i => i.id)
            let listProvider = providers.filter(
                (provider) => ids.includes(provider.id)
            )
            listProvider = listProvider.filter(
                (provider) => provider.location_ids?.includes(selectedLocation)
            )

            if (!isProviderFirst && staffProcedures?.length) {
                const staffIds = staffProcedures?.map((sp) => sp?.staff_id) ?? []
                listProvider = listProvider.filter(({ id }) => staffIds.includes(id))
            }

            console.log('[DBG] big-branch listProvider=', JSON.stringify(listProvider?.map?.(p=>p.id)), 'procs=', procedures?.length, 'selProcs=', selectedProcedures?.length);
            if (procedures?.length === 1 || (appointmentChannelId && selectedProcedures?.length)) {
                loadProviderList(listProvider)
                if (listProvider?.length === 1) {
                    setStaffId(listProvider[0]?.id);
                    handleSelectProvider(null, listProvider[0]?.id);
                    handleProviderContinueBtn()
                } else {
                    document
                        .querySelector('.providers__container')
                        ?.classList?.add('provider-in-booking');
                    renderContinueBtn();
                    providerListeners();
                    if (appointmentChannelId) {
                        document.querySelector('.providers__container .providerContinue__btn ')?.classList?.add('hide');
                    }
                    document
                        .querySelector('.providers__container .providerBack__btn')?.classList?.add('hiden');
                    navigate(SCREEN.PROVIDERS);
                }
            } else {
                if (listProvider?.length === 0) {
                    if (isFlowForCalendar) {
                        loadProviderList(listProvider);
                        navigate(SCREEN.PROVIDERS);
                        return
                    }
                    const div = document.querySelector('.notfound__container .screen__content')
                    if (div) {
                        div.textContent = t('provider_no_location');
                    }
                    navigate(SCREEN.NOTFOUND_APPOINTMENT)
                    return
                }
                if (isPageHidden('patient_page', hiddenPages)) {
                    patientContinueBtnHandler()
                } else {
                    patientListeners();
                    navigate(SCREEN.PATIENTS);
                }
            }
            return
        }
        let staffIdList = staffProcedures?.map(
            (item) => item?.staff?.id
        );
        let filteredProviders = providers.filter(
            (e) => staffIdList?.indexOf(e.id) !== -1
        );

        const listProvider = filteredProviders.filter(
            (provider) => provider.location_ids?.includes(selectedLocation)
        )
        loadProviderList(listProvider);
        if (listProvider?.length === 1 || staffId) {
            handleSelectProvider(null, +staffId || listProvider[0]?.id);
            handleProviderContinueBtn()
        } else {
            document
                .querySelector('.providers__container')
                ?.classList?.add('provider-in-booking');
            renderContinueBtn();
            providerListeners();
            navigate(SCREEN.PROVIDERS);
        }

    } else {
        if (providers.length > 0) {
            const selectedProviders = providers.filter(
                (i) => i?.location_ids?.includes(selectedLocation)
            );
            const providerContainer = document.querySelector('.providers__container .list');
            if (providerContainer) {
                providerContainer.innerHTML = '';
            }
            loadProviderList(selectedProviders);
            setIsInstantQuote(true);
            renderContinueBtn();
            providerListeners();
            navigate(SCREEN.PROVIDERS);
        } else if (isInInstantQuote) {
            loadCategories(filterProcedures(procedures));
            loadProcedures(filterProcedures(procedures));
            navigate(SCREEN.SEE_ALL_SERVICES);
        }
    }
};